export enum DeckLoadErrorCode {
  Network = "network",
  Timeout = "timeout",
  Http = "http",
  InvalidResponse = "invalid-response",
}

export enum DeckLoadRequestName {
  GetDeckById = "getDeckById",
  GetAllCardsForDeckAndSubdecks = "getAllCardsForDeckAndSubdecks",
  GetAllDataForDeckAndSubdecks = "getAllDataForDeckAndSubdecks",
}

export type DeckLoadRequestOutcome = "success" | "backend-rejected" | "failed";

export interface DeckLoadRequestMetrics {
  requestName: DeckLoadRequestName;
  durationMs: number;
  outcome: DeckLoadRequestOutcome;
  requestAttempts: number;
  httpStatus?: number;
  cfRayId?: string;
}

export type DeckLoadRequestObserver = (requestMetrics: DeckLoadRequestMetrics) => void;

interface DeckLoadErrorOptions {
  message: string;
  code: DeckLoadErrorCode;
  retryable: boolean;
  requestAttempts: number;
  durationMs: number;
  httpStatus?: number;
  cfRayId?: string;
  cause?: unknown;
}

export class DeckLoadError extends Error {
  readonly code: DeckLoadErrorCode;
  readonly retryable: boolean;
  readonly requestAttempts: number;
  readonly durationMs: number;
  readonly httpStatus?: number;
  readonly cfRayId?: string;
  readonly cause?: unknown;

  constructor({ message, code, retryable, requestAttempts, durationMs, httpStatus, cfRayId, cause }: DeckLoadErrorOptions) {
    super(message);
    this.name = "DeckLoadError";
    this.code = code;
    this.retryable = retryable;
    this.requestAttempts = requestAttempts;
    this.durationMs = durationMs;
    this.httpStatus = httpStatus;
    this.cfRayId = cfRayId;
    this.cause = cause;
  }
}

type SleepImplementation = (milliseconds: number) => Promise<void>;

const defaultSleep: SleepImplementation = async (milliseconds) => {
  await new Promise<void>((resolve) => globalThis.setTimeout(resolve, milliseconds));
};

function isRetryableHttpStatus(httpStatus: number): boolean {
  return httpStatus === 408 || httpStatus === 425 || httpStatus === 429 || httpStatus >= 500;
}

export function getDeckLoadTimestampInMilliseconds(): number {
  return globalThis.performance?.now() ?? Date.now();
}

export function reportDeckLoadRequestMetrics(
  requestObserver: DeckLoadRequestObserver | undefined,
  requestMetrics: DeckLoadRequestMetrics
): void {
  try {
    requestObserver?.(requestMetrics);
  } catch {
    // Observability must never change whether a deck load succeeds.
  }
}

interface BuildDeckLoadCompletedEventPropertiesOptions {
  deckId: string;
  loadStartedAtMs: number;
  loadCompletedAtMs: number;
  deckFound: boolean;
  requestsSettledSuccessfully?: boolean;
  requestMetrics: DeckLoadRequestMetrics[];
}

type DeckLoadCompletedEventProperties = Record<string, string | number>;

const deckLoadRequestPropertyPrefixes: Record<DeckLoadRequestName, string> = {
  [DeckLoadRequestName.GetDeckById]: "get_deck_by_id",
  [DeckLoadRequestName.GetAllCardsForDeckAndSubdecks]: "get_all_cards_for_deck_and_subdecks",
  [DeckLoadRequestName.GetAllDataForDeckAndSubdecks]: "get_all_data_for_deck_and_subdecks",
};

export function buildDeckLoadCompletedEventProperties({
  deckId,
  loadStartedAtMs,
  loadCompletedAtMs,
  deckFound,
  requestsSettledSuccessfully = true,
  requestMetrics,
}: BuildDeckLoadCompletedEventPropertiesOptions): DeckLoadCompletedEventProperties {
  // Per-request metrics below also catch backend rejections inside fulfilled promises.
  const hasDeckWithoutRejectedRequests = deckFound && requestsSettledSuccessfully;
  const eventProperties: DeckLoadCompletedEventProperties = {
    deck_id: deckId,
    deck_load_outcome: hasDeckWithoutRejectedRequests ? "success" : "failed",
    total_duration_ms: Math.max(0, Math.round(loadCompletedAtMs - loadStartedAtMs)),
  };

  for (const requestName of Object.values(DeckLoadRequestName)) {
    const metricsForRequest = requestMetrics.filter((requestMetric) => requestMetric.requestName === requestName);
    const lastRequestMetrics = metricsForRequest[metricsForRequest.length - 1];
    const propertyPrefix = deckLoadRequestPropertyPrefixes[requestName];
    if (lastRequestMetrics?.outcome !== "success") eventProperties.deck_load_outcome = "failed";

    eventProperties[`${propertyPrefix}_call_count`] = metricsForRequest.length;
    eventProperties[`${propertyPrefix}_duration_ms`] = metricsForRequest.reduce(
      (totalDurationMs, requestMetric) => totalDurationMs + Math.max(0, Math.round(requestMetric.durationMs)),
      0
    );
    eventProperties[`${propertyPrefix}_outcome`] = lastRequestMetrics?.outcome ?? "not-requested";
    eventProperties[`${propertyPrefix}_request_attempts`] = metricsForRequest.reduce(
      (totalRequestAttempts, requestMetric) => totalRequestAttempts + requestMetric.requestAttempts,
      0
    );
    eventProperties[`${propertyPrefix}_http_status`] = lastRequestMetrics?.httpStatus ?? "missing";
    eventProperties[`${propertyPrefix}_cf_ray_id`] = lastRequestMetrics?.cfRayId ?? "missing";
  }

  return eventProperties;
}

export async function requestDeckByIdEndpoint<T>({
  url,
  body,
  fetchImpl = fetch,
  sleepMs = defaultSleep,
  maxAttempts = 3,
  timeoutMs = 5_000,
}: {
  url: string;
  body: Record<string, unknown>;
  fetchImpl?: typeof fetch;
  sleepMs?: SleepImplementation;
  maxAttempts?: number;
  timeoutMs?: number;
}): Promise<{
  data: T;
  requestAttempts: number;
  durationMs: number;
  httpStatus: number;
  cfRayId?: string;
  lastRetryError?: DeckLoadError;
}> {
  let lastError: DeckLoadError | null = null;
  const requestStartedAtMs = getDeckLoadTimestampInMilliseconds();

  const getRequestDurationMs = () =>
    Math.max(0, Math.round(getDeckLoadTimestampInMilliseconds() - requestStartedAtMs));

  for (let attemptNumber = 1; attemptNumber <= maxAttempts; attemptNumber += 1) {
    const abortController = new AbortController();
    const timeoutId = globalThis.setTimeout(() => abortController.abort(), timeoutMs);

    try {
      const response = await fetchImpl(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
        signal: abortController.signal,
      });
      const responseText = await response.text();
      const cfRayId = response.headers?.get("cf-ray") || undefined;

      if (!response.ok) {
        const retryable = isRetryableHttpStatus(response.status);
        lastError = new DeckLoadError({
          message: "Deck endpoint returned an HTTP error",
          code: DeckLoadErrorCode.Http,
          retryable,
          requestAttempts: attemptNumber,
          durationMs: getRequestDurationMs(),
          httpStatus: response.status,
          cfRayId,
        });
        if (retryable && attemptNumber < maxAttempts) {
          await sleepMs(400 * attemptNumber);
          continue;
        }
        throw lastError;
      }

      try {
        return {
          data: JSON.parse(responseText) as T,
          requestAttempts: attemptNumber,
          durationMs: getRequestDurationMs(),
          httpStatus: response.status,
          cfRayId,
          lastRetryError: lastError ?? undefined,
        };
      } catch (error) {
        lastError = new DeckLoadError({
          message: "Deck endpoint returned invalid JSON",
          code: DeckLoadErrorCode.InvalidResponse,
          retryable: true,
          requestAttempts: attemptNumber,
          durationMs: getRequestDurationMs(),
          httpStatus: response.status,
          cfRayId,
          cause: error,
        });
        if (attemptNumber < maxAttempts) {
          await sleepMs(400 * attemptNumber);
          continue;
        }
        throw lastError;
      }
    } catch (error) {
      if (error instanceof DeckLoadError) {
        throw error;
      }

      const didTimeOut = abortController.signal.aborted;
      lastError = new DeckLoadError({
        message: didTimeOut ? "Deck endpoint request timed out" : "Deck endpoint request failed",
        code: didTimeOut ? DeckLoadErrorCode.Timeout : DeckLoadErrorCode.Network,
        retryable: true,
        requestAttempts: attemptNumber,
        durationMs: getRequestDurationMs(),
        cause: error,
      });
      if (attemptNumber < maxAttempts) {
        await sleepMs(400 * attemptNumber);
        continue;
      }
      throw lastError;
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  }

  throw lastError ?? new Error("Deck endpoint request failed before an attempt was made");
}
